/**
 * Rio sketches the bar for a recruiter's question: the area it belongs to
 * and what an excellent, adequate and weak answer shows. It happens once,
 * when a question has none yet (just added, or saved before schema 2), with
 * one call to Rio per question (draft_bar), and the sketch lands on the config
 * as if Rio had typed it in, counting when the bar is complete. The recruiter
 * then edits any line in place. Rio's note on a bar it couldn't finish is kept
 * for the card to show.
 */
import { useEffect, useRef, useState } from 'react';
import type {
  InterviewerConfig,
  Posting,
  SectionKey,
} from '../../services/interviewer.types';
import { useInterviewerService } from '../../interviewer/serviceContext';
import { setExpectations } from '../../interviewer/model';

interface Options {
  config: InterviewerConfig;
  /** The job as written so far: Rio writes the bar with its title and skills. */
  posting: Posting;
  onChange: (change: (current: InterviewerConfig) => InterviewerConfig) => void;
  /** The questions to sketch: those without a bar. */
  enabled: boolean;
  onSketching?: (ids: Set<string>) => void;
}

export const useQuestionBar = ({
  config,
  posting,
  onChange,
  enabled,
  onSketching,
}: Options) => {
  const service = useInterviewerService();
  const inFlight = useRef(new Set<string>());
  // A reply after unmount is dropped; a question removed meanwhile is a no-op.
  const mounted = useRef(true);
  const configRef = useRef(config);
  configRef.current = config;
  const postingRef = useRef(posting);
  postingRef.current = posting;
  const [notes, setNotes] = useState<Record<string, string>>({});

  // Only the questions still without a bar. The in-flight set is deliberately
  // not part of this key: telling the card "sketching" re-renders it, and a
  // key that changed with it would clean this effect up and discard the reply.
  const pending = config.customQuestions
    .filter((q) => !q.expectations)
    .map((q) => q.id)
    .join();

  useEffect(() => {
    if (!enabled || !pending) return;
    const ids = pending.split(',').filter((id) => !inFlight.current.has(id));
    if (!ids.length) return;
    ids.forEach((id) => inFlight.current.add(id));
    onSketching?.(new Set(inFlight.current));
    ids.forEach(async (id) => {
      const question = configRef.current.customQuestions.find(
        (q) => q.id === id,
      );
      try {
        if (!question) return;
        const bar = await service.draftBar(
          { text: question.text, section: question.section },
          postingRef.current,
        );
        if (!mounted.current) return;
        // By id: a question removed meanwhile is a no-op, and one that has a
        // bar by now keeps it (setExpectations).
        onChange((current) => setExpectations(current, id, bar));
        setNotes((all) => {
          const next = { ...all };
          delete next[id];
          if (bar.note) next[id] = bar.note;
          return next;
        });
      } catch {
        // No sketch: the recruiter can write the bar by hand, and Rio writes
        // one on save for a question still without.
      } finally {
        inFlight.current.delete(id);
        if (mounted.current) onSketching?.(new Set(inFlight.current));
      }
    });
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pending, enabled, service]);

  useEffect(() => {
    mounted.current = true;
    const flying = inFlight.current;
    return () => {
      mounted.current = false;
      flying.clear();
    };
  }, []);

  return { notes };
};

export type { SectionKey };
